import React, { useState, useEffect, useContext } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import CategoryList from "./CategoryList";
import CategoryCard from "./CategoryCard";
import ProductCard from "./ProductCard";
import "./CategoryProductList.css";
import { LoadingContext } from "../LoadingContext";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function CategoryProductList() {
  const { categoryID } = useParams();
  const [category, setCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const { setIsLoading } = useContext(LoadingContext);
  const [isExpandedView] = useState(true);
  const [promoMap, setPromoMap] = useState({});

  useEffect(() => {
    const fetchCategory = async () => {
      setIsLoading(true);
      try {
        const categoryResponse = await fetch(
          `${API_BASE_URL}/api/categories/${categoryID}`
        );
        if (!categoryResponse.ok) {
          throw new Error(
            `Failed to fetch category: ${categoryResponse.statusText}`
          );
        }
        const categoryData = await categoryResponse.json();
        setCategory(categoryData);

        // If the category has subcategories, fetch them
        if (
          categoryData.subCategories &&
          categoryData.subCategories.length > 0
        ) {
          const subcategoriesResponse = await fetch(
            `${API_BASE_URL}/api/categories/${categoryID}/subcategories`
          );
          if (!subcategoriesResponse.ok) {
            throw new Error(
              `Failed to fetch subcategories: ${subcategoriesResponse.statusText}`
            );
          }
          const subcategoriesData = await subcategoriesResponse.json();
          setSubcategories(subcategoriesData);
          setProducts([]);
        } else {
          // If the category doesn't have subcategories, fetch the products
          const productsResponse = await fetch(
            `${API_BASE_URL}/products?categoryId=${categoryID}&productType=selling`
          );
          if (!productsResponse.ok) {
            throw new Error(
              `Failed to fetch products: ${productsResponse.statusText}`
            );
          }
          const productsData = await productsResponse.json();
          setProducts(productsData.products || []);
          setSubcategories([]);
        }
      } catch (error) {
        // Silent fail (previously only 1 console.error line in this catch block, no other actions)
      } finally {
        setIsLoading(false);
      }
    };

    const fetchCategoriesTree = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/categories/tree`
        );
        if (!response.ok) {
          throw new Error(
            `Failed to fetch categories tree: ${response.statusText}`
          );
        }
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        // Silent fail (previously only 1 console.error line in this catch block, no other actions)
      }
    };

    fetchCategory();
    fetchCategoriesTree();

    fetch(`${API_BASE_URL}/api/Promotions`)
      .then(r => r.ok ? r.json() : [])
      .then(promos => {
        const map = {};
        promos.forEach(p => {
          // Standalone promos are not catalogue products - they have no id to key on
          if (!p.productId && !p.variantId) return;
          map[p.variantId ? `v${p.variantId}` : `p${p.productId}`] = p.promotionPrice;
        });
        setPromoMap(map);
      })
      .catch(() => {});
  }, [categoryID, setIsLoading]);


  return (
    <div className="category-product-main-container">
      {/* Breadcrumb Section */}
      <div className="category-product-breadcrumb">
        <Link to="/">Home</Link>{" "}
        &gt; <Link to="/category">Our Products</Link> &gt;{" "}
        <span>{category ? category.name : "Loading..."}</span>
      </div>

      <div className="category-product-container">
        {/* Category List Section*/}
        <aside className={`category-product-list-sidebar ${!isExpandedView ? 'collapsed' : ''}`}>
          <div className="category-product-list-top">
            <h3 className="category-product-list-heading">Categories</h3>
          </div>
          {isExpandedView && (
            <CategoryList key="category-product-list" categories={categories} />
          )}
        </aside>

        {/* Product/Subcategory Section */}
        <main className={`category-product-list-container ${!isExpandedView ? 'expanded' : ''}`}>
          <h2 className="category-product-category-header">
            {category ? category.name : "Loading..."}
          </h2>

          {subcategories.length > 0 || products.length > 0 ? (
            <ul className="category-product-grid">
              {subcategories.length > 0 ? (
                subcategories.map((subcategory) => (
                  <li key={subcategory.categoryID} className="category-product-card">
                    <CategoryCard category={subcategory} />
                  </li>
                ))
              ) : (

                products.map((product) => (
                  <li key={product.productID} className="category-product-card">
                    <ProductCard product={product} promoPrice={promoMap[`p${product.productID}`]} />
                  </li>
                ))
              )}
            </ul>
          ) : (
            // Empty State Section
            <div className="category-product-empty">
              <p>No products or subcategories found.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default CategoryProductList;